import React, { useState, useEffect } from 'react';
import { Typography, Box, Tabs, Tab, IconButton } from '@mui/material';
import { IBrand, ErrorCode } from '../../utils/entities/interface';
import { SmartFilter } from '../../components/common/SmartFilter';
import Pagination from '../../components/atoms/Pagination';
import { useLocation, useParams } from 'react-router-dom';
import { GETErrorCodes } from '../../services/brands';
import { debounce } from '../../utils/helper';
import { CreateTableHeader } from '../../components/common/TableHeader';
import { headerErrorCodesTable } from '../../utils/constants';
import { ErrorCodeList } from '../../components/ui/list/ErrorCodes';
import { CreateErrorCodeComp } from '../../components/ui/add/errorcode';
import withComponentAuth from '../../hoc/withComponentAuth';
import ErrorcodeFilter from '../../components/ui/filters/errorcode';
import { AddEditErrorcodeModal } from '../../components/ui/add/errorcodeModal';
import { ArrowBack } from '@mui/icons-material';
import { Backdoor } from '../../components/common/Backdoor';
import { usePaginationParams } from '../../hooks/usePaginationParams';
import { useScrollRestoration } from '../../hooks/useScrollRestoration';

const ManageBrand = () => {
  const { brandId } = useParams();
  const location = useLocation();
  const { brandName } = location.state || {};
  const {
    page,
    rowsPerPage,
    setPage,
    setRowsPerPage,
    searchParams,
    setSearchParams,
  } = usePaginationParams();
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
  const [sort, setSort] = useState<'ASC' | 'DESC'>('ASC');
  const [isLoading, setIsLoading] = useState(true);
  const scrollRef = useScrollRestoration<HTMLDivElement>(!isLoading);
  const [appliedFilter, setAppliedFilter] = useState<string | null>(null);
  const [filterFlag, setFilterFlag] = useState<boolean>(false);
  const [errorCodes, setErrorCodes] = useState<ErrorCode[]>([]);
  const [tabValue, setTabValue] = useState<string>(
    () => searchParams.get('tab') ?? 'Split AC',
  );
  const [openAddModal, setOpenAddModal] = useState<boolean>(false);
  const [errorData, setErrorData] = useState('');

  useEffect(() => {
    if (!brandId) return;

    fetchErrorCodes(
      brandId,
      page,
      rowsPerPage,
      searchQuery,
      filterFlag && appliedFilter ? appliedFilter : undefined,
      tabValue,
    );
  }, [
    brandId,
    page,
    rowsPerPage,
    searchQuery,
    sort,
    tabValue,
    filterFlag,
    appliedFilter,
  ]);

  const fetchErrorCodes = async (
    brandId: string,
    page: number,
    limit: number,
    searchQuery?: string,
    category?: string,
    acType?: string,
  ) => {
    try {
      setIsLoading(true);
      const response = await GETErrorCodes({
        page,
        limit,
        searchQuery,
        sortOrder: sort,
        brandId,
        category,
        acType,
      });
      setErrorCodes(response.data); // Set the user data from the API
      setTotalCount(response.count); // Set the total number of users
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage + 1); // API expects 1-based page index
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const refeshList = () => {
    if (!brandId) return;
    fetchErrorCodes(
      brandId,
      page,
      rowsPerPage,
      searchQuery,
      filterFlag && appliedFilter ? appliedFilter : undefined,
      tabValue,
    );
  };

  const handleSaveRefresh = () => {
    refeshList();
  };

  // Debounced search handler
  const handleSearchRefresh = debounce((search: string) => {
    setSearchQuery(search); // Update the search query state
    setPage(1); // Reset to the first page for new search
  }, 500); // 500ms delay before search executes

  const handleFilterRefresh = (filter: any) => {
    console.log('FILTER HAPPENED', filter);
    if (filter && filter.category) {
      setFilterFlag(true);
      setAppliedFilter(filter.category);
    }
  };

  const handleSortRefresh = () => {
    setSort(sort == 'ASC' ? 'DESC' : 'ASC');
    refeshList();
  };

  const handleresetFilter = () => {
    setAppliedFilter(null);
    setFilterFlag(false);
    setPage(1);
  };

  const handleTabChange = (event: any, newValue: string) => {
    setFilterFlag(false);
    setAppliedFilter(null);
    setTabValue(newValue);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', newValue);
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  };

  const handleAddModal = () => {
    setOpenAddModal(true);
  };
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '100%',
      }}
    >
      {/* <IconButton
        onClick={() => window.history.back()}
        sx={{ mr: 2, color: '#FFFFFF' }}
      >
        <ArrowBack sx={{ color: 'white' }} />
      </IconButton> */}
      {/* <Divider /> */}
      <SmartFilter
        enableBack={true}
        title={`${brandName} Errorcodes`}
        addHeader="Add ErrorCode"
        addbuttontitle="Add Errorcode"
        filterHeader={'Filter Errorcodes'}
        createFilter={['SEARCH', 'ADD', 'FILTER']}
        filterElement={
          <ErrorcodeFilter
            onResetFilter={handleresetFilter}
            category={appliedFilter}
          />
        }
        onSearch={handleSearchRefresh}
        onSaved={handleSaveRefresh}
        addDrawerSize="md"
        onFiltered={handleFilterRefresh}
        onNavigate={handleAddModal}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          p: 1,
        }}
      >
      {brandId && (
        <AddEditErrorcodeModal
          errorcode={errorData}
          open={openAddModal}
          brandId={brandId}
          onClose={() => setOpenAddModal(false)}
          onSaved={() => {
            handleSaveRefresh();
            setOpenAddModal(false);
          }}
        />
      )}
      <Box display="flex" justifyContent="center" width="100%" my={3} px={2}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            backgroundColor: '#1B1B1B',
            borderRadius: '14px',
            padding: '6px',
            minHeight: '56px',

            '& .MuiTabs-indicator': {
              display: 'none',
            },

            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '15px',
              fontWeight: 500,
              color: '#C4C4C4',
              minHeight: '44px',
              borderRadius: '10px',
              padding: '8px 24px',
            },

            '& .Mui-selected': {
              backgroundColor: '#E31E25 !important',
              color: '#FFFFFF !important',
            },

            '& .MuiTab-root:hover': {
              backgroundColor: 'transparent',
            },

            '& .MuiTab-root.Mui-focusVisible': {
              outline: 'none',
            },
          }}
        >
          <Tab disableRipple value="Split AC" label="Split AC" />
          <Tab disableRipple value="Window AC" label="Window AC" />
          <Tab disableRipple value="Cassette AC" label="Cassette AC" />
          <Tab disableRipple value="Concealed AC" label="Concealed AC" />
          <Tab disableRipple value="Vrv/vrf AC" label="Vrv/vrf AC" />
          <Tab disableRipple value="Ducted AC" label="Ducted AC" />
          <Tab disableRipple value="Tower AC" label="Tower AC" />
        </Tabs>
      </Box>
      {/* Error Codes Section */}
      {/* <CreateTableHeader tableCell={headerErrorCodesTable} /> */}
      <Box
        ref={scrollRef}
        sx={{
          height: '100%',
          maxHeight: '100%',
          overflowY: 'scroll',
          scrollbarWidth: 'none',
        }}
      >
        {errorCodes.length <= 0 && !isLoading ? (
          <Typography textAlign={'center'} marginTop={'20%'}>
            No Error Codes Found
          </Typography>
        ) : (
          <ErrorCodeList
            setErrorData={setErrorData}
            ErrorCodes={errorCodes}
            onUpdate={refeshList}
            isLoading={isLoading}
            headerCells={headerErrorCodesTable}
            brand={{ name: brandName, brandId: brandId }}
            handleAddModal={() => {
              handleAddModal();
            }}
          />
        )}
      </Box>
      {/* Pagination */}
      <Box display={'block'} bottom={0} right={0}>
        <Pagination
          count={totalCount} // Use totalUsers from API response
          page={page - 1} // Convert 1-based to 0-based page index for the pagination component
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
      </Box>
    </Box>
  );
};

const tabStyles = {
  backgroundColor: '#1B1B1B', // Dark background ONLY for tab container
  borderRadius: '8px', // Rounded corners for tab container
  padding: '6px', // Padding for better spacing
  display: 'inline-flex', // Prevent full width stretching
  '& .MuiTabs-flexContainer': {
    justifyContent: 'center', // Center align tabs
  },
  '& .MuiTabs-indicator': {
    display: 'none', // Hide default underline
  },
  '& .MuiTab-root': {
    color: '#fff', // Default text color
    textTransform: 'none',
    fontSize: '14px',
    padding: '10px 50px',
    minWidth: 'unset',
  },
  '& .Mui-selected': {
    // Red background for selected tab
    color: '#fff !important',
    borderRadius: '8px',
    backgroundColor: '#d32f2f',
  },
};

export default withComponentAuth(ManageBrand);
